import { useOrganisation, useSetOrganisationEmissionFactors } from '@my/directory-api/hooks';
import type { EmissionFactors } from '@my/directory-api';
import { FormSection, Loading, showToast } from '@my/ui';
import { Check, Edit, RotateCcw, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type FactorKey = 'ladenDry' | 'ladenTemperature' | 'emptyDry' | 'emptyTemperature';
type Draft = Record<FactorKey, string>;

const ROWS: { label: 'laden' | 'empty'; dry: FactorKey; temperature: FactorKey }[] = [
  { label: 'laden', dry: 'ladenDry', temperature: 'ladenTemperature' },
  { label: 'empty', dry: 'emptyDry', temperature: 'emptyTemperature' },
];

const KEYS: FactorKey[] = ['ladenDry', 'ladenTemperature', 'emptyDry', 'emptyTemperature'];

function toDraft(f: Pick<EmissionFactors, FactorKey>): Draft {
  return {
    ladenDry: String(f.ladenDry),
    ladenTemperature: String(f.ladenTemperature),
    emptyDry: String(f.emptyDry),
    emptyTemperature: String(f.emptyTemperature),
  };
}

/** "0,95" / "0.95" → 0.95; anything outside (0, 5] is invalid. */
function parse(text: string): number | null {
  const n = Number(text.trim().replace(',', '.'));
  return Number.isFinite(n) && n > 0 && n <= 5 ? n : null;
}

/**
 * Settings → Central Configuration → Sustainability: the CO₂ factors (kg per
 * km, tank-to-wheel) every order's emissions estimate is computed with.
 * Dry vs temperature-controlled follows the order's cargo definition (its
 * Temperature group). Values equal to the default are saved as "follow the
 * default", so a corrected default reaches this organisation too.
 */
export function SustainabilitySection({ organisationId }: { organisationId: string | null }) {
  const { t } = useTranslation('configuration');
  const organisationQuery = useOrganisation(organisationId);
  const save = useSetOrganisationEmissionFactors();
  const factors = organisationQuery.data?.emissionFactors;
  const [draft, setDraft] = useState<Draft | null>(null);

  const editing = draft !== null;
  const parsed = draft ? KEYS.map((k) => parse(draft[k])) : [];
  const invalid = editing && parsed.some((v) => v === null);

  const submit = async (values: Record<FactorKey, number | null>) => {
    if (!organisationId) return;
    try {
      await save.mutateAsync({ organisationId, ...values });
      setDraft(null);
      showToast({ type: 'success', message: t('sustainability.saved') });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('sustainability.failed'),
      });
    }
  };

  const onSave = () => {
    if (!draft || invalid) return;
    void submit({
      ladenDry: parse(draft.ladenDry),
      ladenTemperature: parse(draft.ladenTemperature),
      emptyDry: parse(draft.emptyDry),
      emptyTemperature: parse(draft.emptyTemperature),
    });
  };

  const onReset = () =>
    void submit({ ladenDry: null, ladenTemperature: null, emptyDry: null, emptyTemperature: null });

  const busy = save.isPending;

  return (
    <FormSection
      title={t('sustainability.title')}
      columns={1}
      actions={
        !factors ? null : !editing ? (
          <button
            type="button"
            onClick={() => setDraft(toDraft(factors))}
            aria-label={t('sustainability.edit')}
            title={t('sustainability.edit')}
            className="rounded-sm p-1 text-blue-600 hover:bg-blue-50"
          >
            <Edit className="h-4 w-4" />
          </button>
        ) : (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setDraft(null)}
              disabled={busy}
              aria-label={t('sustainability.cancel')}
              title={t('sustainability.cancel')}
              className="rounded-sm p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-50"
            >
              <X className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={busy || invalid}
              aria-label={t('sustainability.save')}
              title={t('sustainability.save')}
              className="rounded-sm p-1 text-green-600 hover:bg-green-50 disabled:opacity-50"
            >
              <Check className="h-4 w-4" />
            </button>
          </div>
        )
      }
    >
      {organisationQuery.isPending && <Loading label={t('sustainability.title')} />}
      {factors && (
        <div className="space-y-3">
          <p className="max-w-3xl text-xs text-slate-600">{t('sustainability.intro')}</p>

          <div className="overflow-x-auto">
            <table className="text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-slate-400">
                  <th className="py-1 pr-6 text-left font-semibold">{t('sustainability.unit')}</th>
                  <th className="px-3 py-1 text-left font-semibold">{t('sustainability.dry')}</th>
                  <th
                    className="px-3 py-1 text-left font-semibold"
                    title={t('sustainability.temperatureHint')}
                  >
                    {t('sustainability.temperature')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => (
                  <tr key={row.label} className="border-t border-slate-100">
                    <th className="py-1.5 pr-6 text-left text-sm font-medium text-slate-700">
                      {t(`sustainability.${row.label}`)}
                      {row.label === 'empty' && (
                        <span className="block text-[10px] font-normal text-slate-400">
                          {t('sustainability.emptyHint')}
                        </span>
                      )}
                    </th>
                    {[row.dry, row.temperature].map((key) => (
                      <td key={key} className="px-3 py-1.5">
                        {editing ? (
                          <input
                            inputMode="decimal"
                            aria-label={`${t(`sustainability.${row.label}`)} ${t(
                              `sustainability.${key === row.dry ? 'dry' : 'temperature'}`,
                            )}`}
                            value={draft[key]}
                            onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                            className={`w-20 rounded-sm border px-2 py-1 text-right font-mono text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                              parse(draft[key]) === null ? 'border-red-400' : 'border-slate-300'
                            }`}
                          />
                        ) : (
                          <span className="font-mono tabular-nums text-slate-900">
                            {factors[key].toFixed(2)}
                            {factors[key] === factors.defaults[key] && (
                              <span className="ml-1.5 font-sans text-[10px] text-slate-400">
                                {t('sustainability.default')}
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {invalid && <p className="text-xs text-red-600">{t('sustainability.invalid')}</p>}

          <p className="max-w-3xl text-[11px] text-slate-400">{t('sustainability.defaultsNote')}</p>

          {editing && !factors.isDefault && (
            <button
              type="button"
              onClick={onReset}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {t('sustainability.reset')}
            </button>
          )}
        </div>
      )}
    </FormSection>
  );
}
